import { useContext, useMemo } from "react";
import { MarketExpansionFiltersContext } from "@app/overviews/market-expansion-dashboard/contexts/MarketExpansionFiltersContext";
import { MembershipMapDataContext } from "@app/overviews/market-expansion-dashboard/contexts/MembershipMapDataContext";
import {
  useGetMarketplacePayerPlanCounts,
  useGetMedicaidPayerPlanCounts,
} from "@app/overviews/market-expansion-dashboard/hooks/marketShareQueries";
import { MarketExpansionLineOfBusinessEnum } from "@app/overviews/market-expansion-dashboard/shared/MarketExpansionLineOfBusinessEnum";
import {
  buildMarketShareChartData,
  PayerCount,
} from "@app/overviews/market-expansion-dashboard/shared/marketShareHelper";
import { MarketShareChartData } from "@app/overviews/market-expansion-dashboard/shared/MarketShareChartData";
import { MarketplaceMarketShareFilterCriteria } from "@app/overviews/market-expansion-dashboard/shared/MarketplaceMarketShareFilterCriteria";
import { MedicaidMarketShareFilterCriteria } from "@app/overviews/market-expansion-dashboard/shared/MedicaidMarketShareFilterCriteria";
import { useDebouncedValue } from "@app/shared/hooks/useDebouncedValue";
import { UserContext } from "@app/user/UserContext";

const COUNTY_SELECTION_DEBOUNCE_MS = 1000;

interface MarketShareChartResult {
  data: MarketShareChartData[];
  hasData: boolean;
  isError: boolean;
  isLoading: boolean;
  total: number;
}

const EMPTY_RESULT: MarketShareChartResult = { data: [], hasData: false, isError: false, isLoading: false, total: 0 };

/**
 * Builds Market Share pie chart data for whichever line of business is selected:
 *
 * - Medicare derives payer/enrollment pairs from data MembershipMapDataContext already fetched
 *   for the whole state, filtered against the debounced county selection.
 *
 * - Marketplace and Medicaid each fetch distinct payer plan counts for the selected Service Area
 *   via their own query (see marketShareQueries.ts), also filtered against the debounced selection.
 *
 *
 * All branches read the debounced selection (rather than the raw one) so that rapid
 * county-selection changes coalesce into a single chart update/animation instead of one per click.
 */
function useMarketShareChartData(): MarketShareChartResult {
  const filtersContext = useContext(MarketExpansionFiltersContext);
  const membershipContext = useContext(MembershipMapDataContext);
  const userContext = useContext(UserContext);
  const lineOfBusiness = filtersContext.selectedLineOfBusiness;

  // Debounced together so a state change can't transiently pair the new state's code with the
  // previous state's still-debounced counties (they always settle on the same selection at once).
  const selection = useMemo(
    () => ({ selectedCounties: filtersContext.selectedCounties, stateCode: filtersContext.selectedStateAbbreviation }),
    [filtersContext.selectedCounties, filtersContext.selectedStateAbbreviation],
  );
  const debouncedSelection = useDebouncedValue(selection, COUNTY_SELECTION_DEBOUNCE_MS);
  const debouncedSelectedCounties = debouncedSelection.selectedCounties;
  const stateCode = debouncedSelection.stateCode;

  const isMarketplace = lineOfBusiness === MarketExpansionLineOfBusinessEnum.Marketplace;
  const marketplaceRequest: MarketplaceMarketShareFilterCriteria | undefined =
    isMarketplace && stateCode && debouncedSelectedCounties.length > 0
      ? { countySSAs: debouncedSelectedCounties, stateCode }
      : undefined;

  const marketplaceQuery = useGetMarketplacePayerPlanCounts(
    userContext.clientId,
    userContext.userId,
    marketplaceRequest,
  );

  const isMedicaid = lineOfBusiness === MarketExpansionLineOfBusinessEnum.Medicaid;
  const medicaidRequest: MedicaidMarketShareFilterCriteria | undefined =
    isMedicaid && stateCode && debouncedSelectedCounties.length > 0
      ? { countySSAs: debouncedSelectedCounties, stateCode }
      : undefined;

  const medicaidQuery = useGetMedicaidPayerPlanCounts(userContext.clientId, userContext.userId, medicaidRequest);

  return useMemo(() => {
    if (!filtersContext.selectedState || filtersContext.selectedCounties.length === 0) {
      return EMPTY_RESULT;
    }

    switch (lineOfBusiness) {
      case MarketExpansionLineOfBusinessEnum.Marketplace: {
        const payerCounts: PayerCount[] = (marketplaceQuery.data ?? []).map((p) => ({
          count: p.planCount,
          payerName: p.payerName,
        }));
        const { chartData, total } = buildMarketShareChartData(payerCounts);

        return {
          data: chartData,
          hasData: chartData.length > 0,
          isError: marketplaceQuery.isError,
          isLoading: marketplaceQuery.isPending,
          total,
        };
      }

      case MarketExpansionLineOfBusinessEnum.Medicaid: {
        const payerCounts: PayerCount[] = (medicaidQuery.data ?? []).map((p) => ({
          count: p.planCount,
          payerName: p.payerName,
        }));
        const { chartData, total } = buildMarketShareChartData(payerCounts);

        return {
          data: chartData,
          hasData: chartData.length > 0,
          isError: medicaidQuery.isError,
          isLoading: medicaidQuery.isPending,
          total,
        };
      }

      case MarketExpansionLineOfBusinessEnum.Medicare: {
        if (membershipContext.membershipShadedCountyData === undefined) {
          return { ...EMPTY_RESULT, isLoading: true };
        }

        const filteredCounties = membershipContext.countySummaryData.filter((x) =>
          debouncedSelectedCounties.includes(x.countySSA),
        );
        const payerCounts: PayerCount[] = filteredCounties
          .map((x) => x.medicarePayers ?? [])
          .flat()
          .map((p) => ({ count: p.enrollment, payerName: p.payerName }));

        const { chartData, total } = buildMarketShareChartData(payerCounts);
        return {
          data: chartData,
          hasData: chartData.length > 0,
          isError: false,
          isLoading: false,
          total,
        };
      }

      default:
        return EMPTY_RESULT;
    }
  }, [
    filtersContext.selectedState,
    filtersContext.selectedCounties.length,
    lineOfBusiness,
    marketplaceQuery.data,
    marketplaceQuery.isError,
    marketplaceQuery.isPending,
    membershipContext.membershipShadedCountyData,
    membershipContext.countySummaryData,
    debouncedSelectedCounties,
    medicaidQuery.data,
    medicaidQuery.isError,
    medicaidQuery.isPending
  ]);
}

export { useMarketShareChartData, MarketShareChartResult };
